import {Injectable} from '@angular/core';
import {ModeSAML,SamlConfiguration} from "@domain/admin/parametre/authentification/saml-configuration";
import {BehaviorSubject,Observable} from "rxjs";
import {handleResult} from "@share/utils/handle-result";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {Result} from "@domain/common/http/result";
import {MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";
import {CertificatDialogComponent} from "@components/admin/parametre/authentification/saml/certificat-dialog/certificat-dialog.component";
import {ClefPriveeDialogComponent} from "@components/admin/parametre/authentification/saml/clef-privee-dialog/clef-privee-dialog.component";
import {finalize} from "rxjs/operators";
import {SamlHttpService} from "@services/admin/parametre/authentification/saml-http.service";

/**
 * Service de gestion pour le saml
 *
 * @author Lionel Gimenez
 * @date 29/07/2025
 */
@Injectable()
export class SamlService {
    /** Indique si on est en cours de sauvegarde */
    isSaving: boolean = false;

    /** Subject sur PasswordParams */
    private samlSubject: BehaviorSubject<Saml> = new BehaviorSubject<Saml>(null);

    /**
     * Constructeur
     */
    constructor(
        private samlHttpService: SamlHttpService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private matDialog: MatDialog,
    ) {
        //On charge les données
        this.load();
    }

    /**
     * Charge les données
     */
    load(): void {
        this.samlHttpService.load()
            .pipe(handleResult(this.translateService,this.toastrService,null,'global.errors.generic'))
            .subscribe((result: Result) => {
                const saml = result.data;

                //Si on a pas de configuration
                if (saml.config == null) {
                    //On charge une configuration par défaut
                    saml.config = this.initSamlConfig();
                }

                this.samlSubject.next(saml);
            });
    }

    /**
     * Initialisation d'une configuration par défaut
     */
    initSamlConfig(): SamlConfiguration {
        const samlConfiguration: SamlConfiguration = {
            authContext: "",
            defaultClaim: "",
            encryptNameId: false,
            idConfig: 1, //On n'a qu'une seule config et elle peut avoir que l'id 1, c'est historique
            idpUrl: "",
            mode: ModeSAML.SP,
            spCertificate: "",
            spPrivateKey: ""
        };

        return samlConfiguration
    }

    /**
     * Sauvegarde les données
     *
     * @param idpFile Fichier de métadonnées IDP optionnel
     */
    save(idpFile: File): void {
        //On récupère le saml courant
        const saml = this.samlSubject.getValue();

        //On change l'indicateur de sauvegarde
        this.isSaving = true;

        //On le sauvegarde
        this.samlHttpService.save(saml.config,idpFile)
            .pipe(
                handleResult(this.translateService,this.toastrService),
                finalize(() => this.isSaving = false)
            )
            .subscribe();
    }

    /**
     * Ouvre la popin pour changer le certificat
     */
    openCertificatDialog(): void {
        this.matDialog.open(CertificatDialogComponent,{
            data: {
                certificat: this.samlSubject.getValue().config.spCertificate
            }
        }).afterClosed().subscribe((certificat: string) => {
            //Si l'utilisateur n'a pas fermé la fenêtre ou cliqué sur annuler
            if (certificat !== null) {
                this.samlSubject.getValue().config.spCertificate = certificat;
            }
        });
    }

    /**
     * Ouvre la popin pour changer la clef privée
     */
    openClefPriveeDialog(): void {
        this.matDialog.open(ClefPriveeDialogComponent,{
            data: {
                clefPrivee: this.samlSubject.getValue().config.spPrivateKey
            }
        }).afterClosed().subscribe((clefPrivee: string) => {
            //Si l'utilisateur n'a pas fermé la fenêtre ou cliqué sur annuler
            if (clefPrivee !== null) {
                this.samlSubject.getValue().config.spPrivateKey = clefPrivee;
            }
        });
    }

    /**
     * Ouvre les métadonnées
     */
    openMetadonnees(): void {
        this.samlHttpService.metadonnees().subscribe({
            next: (xmlContent) => {
                //Création d'un Blob avec le contenu XML
                const blob = new Blob([xmlContent],{type: 'application/xml'});
                //Création d'une URL pour le blob
                const url = window.URL.createObjectURL(blob);
                //Ouverture d'un nouvel onglet avec l'URL
                window.open(url,'_blank');
                //Libération de l'URL
                setTimeout(() => window.URL.revokeObjectURL(url),100);
            },
            error: (error) => {
                //Affichage d'une erreur en cas d'échec
                this.toastrService.error(this.translateService.instant('global.errors.chargement'));
            }
        });
    }

    /**
     * Retourne un observable de saml
     */
    get(): Observable<Saml> {
        return this.samlSubject.asObservable();
    }
}

/**
 * Interface pour le modèle saml
 */
export interface Saml {
    isSAML: boolean,
    config: SamlConfiguration,
    listeAuthContext: string[]
}